import {createMultiStyleConfigHelpers, defineCssVars} from '@chakra-ui/styled-system';
import {useMultiStyleConfig} from '@chakra-ui/system';
import {mode} from '@chakra-ui/theme-tools';

import {components} from '../../../design-tokens/light-legacy';

import {counterCssVars, counterStyle} from './PaginationCounter';
import {groupCssVars, groupStyle} from './PaginationGroup';
import {listCssVars, listStyle} from './PaginationList';
import {triggerCssVars, triggerStyle} from './PaginationTrigger';

const parts = [`root`, `list`, `trigger`, `counter`, `group`] as const;
export type PaginationParts = (typeof parts)[number];
export const {
  color: $color,
  direction: $direction,
  fontSize: $fontSize,
  padding: $padding,
} = defineCssVars(`pagination`, [`color`, `direction`, `fontSize`, `padding`]);

const {
  PaginationButton: {
    // TODO: retrieve design tokens usage
    baseStyle: {..._baseStyle},
    // TODO (PRE-336): Design transform - Handle component with states only (no variant)
    _active: {_active},
  },
  PaginationContainer: {
    // TODO: retrieve design tokens usage
    baseStyle: {..._baseStyle2},
  },
} = components;

const {defineMultiStyleConfig} = createMultiStyleConfigHelpers(parts);

const PaginationTheme = defineMultiStyleConfig({
  baseStyle: ({dir = `ltr`, ...props}) => {
    const flexDirection = dir === `ltr` ? `row-reverse` : `row`;
    return {
      root: {
        [$color.variable]: mode(`colors.gray.500`, `colors.whiteAlpha.800`)(props),
        [$direction.variable]: flexDirection,
        [$fontSize.variable]: `fontSizes.sm`,
        [$padding.variable]: `space.12`,

        ...counterCssVars(props),
        ...groupCssVars,
        ...triggerCssVars,
        ...listCssVars,
        // An hack so the outline of the pagination trigger wont be cut off
        paddingRight: `2px`,
        py: $padding.reference,
        color: $color.reference,
      },
      list: {
        // Layout
        ...listStyle.baseStyle,
        flexDirection: $direction.reference,
        fontSize: $fontSize.reference,
      },
      group: groupStyle.baseStyle,
      trigger: triggerStyle.baseStyle,
      counter: counterStyle.baseStyle,
      sizes: {
        root: {
          sm: {
            [$fontSize.variable]: `fontSizes.xs`,
          },
          md: {
            [$fontSize.variable]: `fontSizes.sm`,
          },
          lg: {
            [$fontSize.variable]: `fontSizes.md`,
          },
        },
      },
    };
  },
});

export const usePaginationStyleConfig = <Part extends PaginationParts>(
  part: Part,
  props?: Parameters<typeof useMultiStyleConfig>[1],
) => {
  return useMultiStyleConfig(`Pagination`, props ?? {})?.[part] as any;
};

export default PaginationTheme;
